import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Bell, LogOut, User, GraduationCap, Home, ClipboardList, FileText, UserCircle } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBranding } from "@/context/BrandingContext";
import { authService } from "@/services/authService";
import { PortalProvider, usePortal } from "./PortalContext";
import { PortalFooter } from "@/components/layout/Footer";

const TAB_ROUTES = [
    { label: "Home", path: "/portal/home", icon: Home },
    { label: "Tasks", path: "/portal/tasks", icon: ClipboardList },
    { label: "Applications", path: "/portal/applications", icon: FileText },
    { label: "My Account", path: "/portal/account", icon: UserCircle },
];

function PortalLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user: currentUser } = useAuth();
    const { branding, loading } = useBranding();
    const { tasks } = usePortal();

    const pendingTasksCount = tasks.filter(
        (task) => task.status !== "Completed" && task.status !== "Cancelled"
    ).length;

    const universityName = branding?.name || "University Portal";
    const logoUrl = branding?.branding?.logo;
    const primaryColor = branding?.branding?.colors?.primary || "#1d3658";

    const userInitials = currentUser
        ? `${currentUser.first_name?.[0] || ''}${currentUser.last_name?.[0] || ''}`.toUpperCase()
        : 'U';

    const userFullName = currentUser
        ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
        : 'Loading...';

    const handleLogout = async () => {
        await authService.logout();
        navigate("/login");
    };

    const isTabActive = (path: string) => location.pathname.startsWith(path);

    return (
        <div className="min-h-screen bg-slate-50/50 flex flex-col">
            <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center">
                                {loading ? (
                                    <div className="h-8 w-8 bg-slate-200 animate-pulse rounded"></div>
                                ) : logoUrl ? (
                                    <img
                                        src={logoUrl}
                                        alt={`${universityName} Logo`}
                                        className="max-w-full max-h-full object-contain"
                                    />
                                ) : (
                                    <div
                                        className="w-8 h-8 rounded flex items-center justify-center text-white"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        <GraduationCap className="w-5 h-5" />
                                    </div>
                                )}
                            </div>
                            <span
                                className="font-serif font-black text-lg"
                                style={{
                                    color: primaryColor,
                                    fontFamily: "var(--font-university-name)",
                                }}
                            >
                                {universityName}
                            </span>
                        </div>
                        <div className="hidden md:flex items-center gap-6">
                            {TAB_ROUTES.map(({ label, path }) => (
                                <button
                                    key={label}
                                    onClick={() => navigate(path)}
                                    className={`text-sm font-bold transition-colors py-5 border-b-2 ${isTabActive(path) ? "text-primary border-primary" : "text-slate-500 border-transparent hover:text-primary"}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" className="relative text-slate-500" onClick={() => navigate("/portal/tasks")}>
                            <Bell className="h-5 w-5" />
                            {pendingTasksCount > 0 && (
                                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
                            )}
                        </Button>

                        <div className="pl-4 border-l border-slate-200 flex items-center">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-3 outline-none group cursor-pointer">
                                        <div className="text-right hidden sm:block">
                                            <p className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors">
                                                {userFullName}
                                            </p>
                                            <p className="text-xs text-slate-500 italic">
                                                Applicant
                                            </p>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold group-hover:bg-primary group-hover:text-white transition-all ring-2 ring-transparent group-hover:ring-primary/20">
                                            {userInitials}
                                        </div>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    className="w-56 mt-2 rounded-xl p-2 shadow-xl border-slate-100 bg-white z-[100]"
                                >
                                    <DropdownMenuSeparator className="bg-slate-50" />
                                    <DropdownMenuItem
                                        onSelect={handleLogout}
                                        className="flex items-center gap-2 p-3 rounded-lg cursor-pointer text-red-500 hover:bg-red-50 transition-colors group outline-none"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        <span className="font-bold text-sm">Log Out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile bottom navigation */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 flex md:hidden">
                {TAB_ROUTES.map(({ label, path, icon: Icon }) => (
                    <button
                        key={label}
                        onClick={() => navigate(path)}
                        className={`flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs font-bold transition-colors ${
                            isTabActive(path) ? "text-primary" : "text-slate-400"
                        }`}
                        style={isTabActive(path) ? { color: primaryColor } : undefined}
                    >
                        <Icon className="w-5 h-5" />
                        <span>{label}</span>
                    </button>
                ))}
            </nav>

            <main className="flex-1 container mx-auto px-4 py-8 pb-24 md:pb-8">
                <div className="flex flex-col gap-8">
                    <Outlet />
                </div>
            </main>

            <div className="pb-24 md:pb-0">
                <PortalFooter />
            </div>
        </div>
    );
}

export default function Portal() {
    return (
        <PortalProvider>
            <PortalLayout />
        </PortalProvider>
    );
}
